import * as Overview from "./overviewModel"
import * as Viewings from "./viewingsModel"
import * as Feedback from "./feedbackModel"
import * as Rentals from "./rentalsModel"
import * as E from "fp-ts/lib/Either"
import * as O from "fp-ts/lib/Option"
import { HttpError } from "elm-ts/lib/Http"
import {
  Account,
  Dashboard,
  DealOverview,
  DealViewings,
  DealRentals,
  DealFeedback,
  Variant,
} from "@flypengine/myFlyp-lib"
import * as FirebaseAuth from "firebase/auth"
import * as Router from "../../routes"
import { PageStatus } from "../../lib/types"

export type Config<msg> = {
  wrapperMsg: (m: Msg) => msg
  logoutMsg: () => msg
  route: Router.Route
  user: FirebaseAuth.User
}

export type Model = {
  tab: O.Option<Router.Tab>
  navigation: DashboardNavigation
  dashboard: E.Either<PageStatus, Dashboard>
  properties: E.Either<PageStatus, Account["properties"]>
  overviewModel: Overview.Model
  viewingsModel: Viewings.Model
  feedbackModel: Feedback.Model
  rentalsModel: Rentals.Model
}

export const _tabChanged = "tabChanged"
export const _overviewMsg = "overviewMsg"
export const _viewingsMsg = "viewingsMsg"
export const _feedbackMsg = "feedbackMsg"
export const _rentalsMsg = "rentalsMsg"
export const _dashboardChanged = "dashboardChanged"
export const _updateNavigation = "updateNavigation"
export const _initDashboard = "initDashboard"
export const _dashboardReq = "dashboardReq"
export const _dashboardRes = "dashboardRes"
export const _dealOverviewReq = "dealOverviewReq"
export const _dealOverviewRes = "dealOverviewRes"
export const _dealViewingsReq = "dealViewingsReq"
export const _dealViewingsRes = "dealViewingsRes"
export const _dealFeedbackReq = "dealFeedbackReq"
export const _dealFeedbackRes = "dealFeedbackRes"
export const _dealRentalsReq = "dealRentalsReq"
export const _dealRentalsRes = "dealRentalsRes"
export const _accountReq = "accountReq"
export const _accountRes = "accountRes"

export type Msg =
  | Variant.Tagged<typeof _tabChanged, Router.Tab>
  | Variant.Tagged<typeof _overviewMsg, Overview.Msg>
  | Variant.Tagged<typeof _viewingsMsg, Viewings.Msg>
  | Variant.Tagged<typeof _feedbackMsg, Feedback.Msg>
  | Variant.Tagged<typeof _initDashboard, E.Either<HttpError, Account>>
  | Variant.Tagged<typeof _rentalsMsg, Rentals.Msg>
  | Variant.Tagged<typeof _dashboardChanged, string>
  | Variant.Tagged<typeof _updateNavigation, DashboardNavigation>
  | Variant.Tagged<typeof _dashboardReq, string>
  | Variant.Tagged<typeof _dashboardRes, E.Either<HttpError, Dashboard>>
  | Variant.Tagged<typeof _dealOverviewReq, string>
  | Variant.Tagged<typeof _dealOverviewRes, E.Either<HttpError, DealOverview>>
  | Variant.Tagged<typeof _dealViewingsReq, void>
  | Variant.Tagged<typeof _dealViewingsRes, E.Either<HttpError, DealViewings>>
  | Variant.Tagged<typeof _dealFeedbackReq, void>
  | Variant.Tagged<typeof _dealFeedbackRes, E.Either<HttpError, DealFeedback>>
  | Variant.Tagged<typeof _accountReq, void>
  | Variant.Tagged<typeof _accountRes, E.Either<HttpError, Account>>
  | Variant.Tagged<typeof _dealRentalsReq, void>
  | Variant.Tagged<typeof _dealRentalsRes, E.Either<HttpError, DealRentals>>
export const Msg = Variant.module<Msg>({
  tabChanged: value => Variant.tagged(_tabChanged, value),
  overviewMsg: value => Variant.tagged(_overviewMsg, value),
  viewingsMsg: value => Variant.tagged(_viewingsMsg, value),
  feedbackMsg: value => Variant.tagged(_feedbackMsg, value),
  rentalsMsg: value => Variant.tagged(_rentalsMsg, value),
  dashboardChanged: value => Variant.tagged(_dashboardChanged, value),
  updateNavigation: value => Variant.tagged(_updateNavigation, value),
  initDashboard: value => Variant.tagged(_initDashboard, value),
  dashboardReq: value => Variant.tagged(_dashboardReq, value),
  dashboardRes: value => Variant.tagged(_dashboardRes, value),
  dealOverviewReq: value => Variant.tagged(_dealOverviewReq, value),
  dealOverviewRes: value => Variant.tagged(_dealOverviewRes, value),
  dealViewingsReq: value => Variant.tagged(_dealViewingsReq, value),
  dealViewingsRes: value => Variant.tagged(_dealViewingsRes, value),
  dealFeedbackReq: value => Variant.tagged(_dealFeedbackReq, value),
  dealFeedbackRes: value => Variant.tagged(_dealFeedbackRes, value),
  dealRentalsReq: value => Variant.tagged(_dealRentalsReq, value),
  dealRentalsRes: value => Variant.tagged(_dealRentalsRes, value),
  accountReq: value => Variant.tagged(_accountReq, value),
  accountRes: value => Variant.tagged(_accountRes, value),
})

export const configOverview = (propcode: string): Overview.Config<Msg> => ({
  propcode,
  wrapperMsg: Msg.overviewMsg,
  tabChangedMsg: Msg.tabChanged,
  dashboardChanged: Msg.dashboardChanged,
  dealOverviewMsg: Msg.dealOverviewReq,
})

export const configViewings = (
  user: FirebaseAuth.User,
  propcode: string
): Viewings.Config<Msg> => ({
  user,
  propcode,
  wrapperMsg: Msg.viewingsMsg,
  dealViewingsMsg: Msg.dealViewingsReq,
})

export const configFeedback = (
  user: FirebaseAuth.User,
  propcode: string
): Feedback.Config<Msg> => ({
  user,
  propcode,
  wrapperMsg: Msg.feedbackMsg,
  dealFeedbackMsg: Msg.dealFeedbackReq,
})

export const configRentals = (
  user: FirebaseAuth.User,
  propcode: string
): Rentals.Config<Msg> => ({
  propcode,
  user,
  wrapperMsg: Msg.rentalsMsg,
  dealRentalsMsg: Msg.dealRentalsReq,
})

type DashboardNavigation = { tab: Router.Tab; enabled: boolean }[]

export const initNavigation = (
  features: Dashboard["features"]
): DashboardNavigation => {
  const navigation: DashboardNavigation = [
    { tab: "overview", enabled: false },
    { tab: "viewings", enabled: false },
    { tab: "feedback", enabled: false },
    { tab: "rentals", enabled: false },
  ]

  return navigation.map(item => {
    switch (item.tab) {
      case "overview": {
        return {
          ...item,
          enabled: features.includes("overview"),
        }
      }
      case "viewings": {
        return {
          ...item,
          enabled: features.includes("viewings"),
        }
      }
      case "feedback": {
        return {
          ...item,
          enabled: features.includes("agentFeedback"),
        }
      }
      case "rentals": {
        return {
          ...item,
          enabled: features.includes("rentals"),
        }
      }
    }
  })
}
